// src/components/AddGuidanceModal.js
// Updated: 10/31/2025 08:05 PM EDT
// --- THIS IS THE FULL, UPDATED FILE ---

import React, { useState, useCallback } from 'react';
import { db, storage } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import {
  collection,
  doc,
  setDoc,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { X, UploadCloud, FileText } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

export default function AddGuidanceModal({ isOpen, onClose, session }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const { currentUser, userData } = useAuth();

  const resetForm = () => {
    setFile(null);
    setTitle('');
    setIsUploading(false);
    setProgress(0);
    setError('');
  };

  const onDrop = useCallback((acceptedFiles) => {
    const droppedFile = acceptedFiles[0];
    if (!droppedFile) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(droppedFile.type)) {
      setError('Invalid file type. Please upload a PDF, JPG, or PNG.');
      return;
    }

    setFile(droppedFile);
    if (!title.trim()) {
      setTitle(droppedFile.name.replace(/\.[^/.]+$/, ""));
    }
    setError('');
  }, [title]);

  const handleUploadAndLink = () => {
    if (!file) return setError('Please select a file.');
    if (!title.trim()) return setError('Please enter a title.');
    if (!currentUser || !userData?.orgId) return setError('User data missing.');
    if (!session || !session.reviewers) return setError('Session data is incomplete.'); // Safety check

    setIsUploading(true);
    setError('');

    const fileId = uuidv4();
    const storagePath = `organizations/${userData.orgId}/guidance/${fileId}/${file.name}`;
    const fileRef = storageRef(storage, storagePath);
    const metadata = {
      contentType: file.type,
      customMetadata: {
        orgId: userData.orgId,
        uploadedBy: currentUser.uid,
      },
    };

    const uploadTask = uploadBytesResumable(fileRef, file, metadata);

    uploadTask.on(
      'state_changed',
      (snapshot) => setProgress((snapshot.bytesTransferred / snapshot.totalBytes) * 100),
      (err) => {
        console.error("Upload error:", err);
        setError("File upload failed. Check storage permissions.");
        setIsUploading(false);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          // 1. Create the main document in /documents
          const newDocRef = await addDoc(collection(db, 'documents'), {
            orgId: userData.orgId,
            uploaderId: currentUser.uid,
            uploaderName: currentUser.displayName,
            title: title,
            fileName: file.name,
            fileType: file.type,
            isPublic: false, 
            context: 'guidance',
            storagePath: storagePath,
            downloadURL: downloadURL,
            uploadedAt: serverTimestamp(),
            // --- NEW FIELDS FOR FILTERING ---
            linkedSessionId: session.id, 
            assignedReviewers: session.reviewers, // <-- Use the list from the session
            // --- END NEW FIELDS ---
          });

          // 2. Link it to the scoring session
          const linkDocRef = doc(db, 'scoringSessions', session.id, 'guidanceDocs', newDocRef.id);
          await setDoc(linkDocRef, {
            documentId: newDocRef.id,
            title: title,
            uploadedAt: serverTimestamp(),
            orgId: userData.orgId,
          });

          resetForm();
          onClose();
        } catch (dbError) {
          console.error("Error creating Firestore docs:", dbError);
          setError("Upload succeeded but failed to link document.");
          setIsUploading(false);
        }
      }
    );
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled: isUploading,
  });

  const handleClose = () => {
    if (isUploading) return;
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <button onClick={handleClose} disabled={isUploading} className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 disabled:opacity-50">
          <X className="h-6 w-6" />
        </button>
        <h2 className="text-xl font-semibold text-gray-900">Upload Guidance Document</h2>
        <p className="mt-1 text-sm text-gray-500">This file will be visible to all reviewers in session **{session?.name}**.</p>

        <div className="mt-4 space-y-4">
          
          {/* Document Title Input */}
          <div>
            <label htmlFor="doc-title" className="block text-sm font-medium text-gray-700">Document Title</label>
            <input
              type="text"
              id="doc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={isUploading}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              placeholder="e.g., 'Reviewer Instructions'"
            />
          </div>

          {/* File Dropzone */}
          <div
            {...getRootProps()}
            className={`flex h-40 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed ${
              isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
            }`}
          >
            <input {...getInputProps()} />
            {file ? (
              <div className="text-center text-green-600"><FileText className="mx-auto h-10 w-10" /><p className="mt-2 text-sm font-medium">{file.name}</p></div>
            ) : (
              <div className="text-center text-gray-500"><UploadCloud className="mx-auto h-10 w-10" /><p className="mt-2 text-sm">Drag & drop file or click to select</p></div>
            )}
          </div>

          {isUploading && (
            <div>
              <div className="mt-1 w-full rounded-full bg-gray-200">
                <div
                  className="rounded-full bg-blue-600 p-0.5 text-center text-xs font-medium leading-none text-blue-100"
                  style={{ width: `${progress}%` }}
                >
                  {Math.round(progress)}%
                </div>
              </div>
            </div>
          )}

          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

          <div className="flex justify-end pt-4">
            <button
              type="button"
              onClick={handleUploadAndLink}
              disabled={isUploading || !file || !title.trim()}
              className="rounded-md border border-transparent bg-blue-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
            >
              {isUploading ? 'Processing...' : 'Upload & Attach'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
